import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { AccessGroup } from '@/api/auth';
import type { ConsiderationAfterAction } from '@/api/considerations';
import type { ApiError } from '@/api/http';
import type { ConsiderationOnCard } from '@/api/people';
import { queryKeyRoots } from '@/api/queryKeys';
import { mayWriteAboutPerson } from '@/pages/Candidates/candidateAccess';

import { actionsFor, STATUS_ACTIONS, type StatusAction } from './statusActions';

/**
 * The 409's own field, read through the one mechanism this API has for a refusal body
 * (`ApiError.bodyWith`). Its **type is derived** rather than written out: `.claude/rules/frontend.md`
 * — types come from the OpenAPI document, and `api.test.ts` fails a hand-written one.
 */
const readExistingRun = (error: ApiError | null) =>
  error?.bodyWith('existing_consideration')?.existing_consideration;

export interface StatusActionState {
  /**
   * Whether the actions are rendered at all — §5's rule on a card: an action a group may not use is
   * **not rendered**, never disabled. The evidence is the server's (four rows of
   * `backend/tests/test_permission_matrix.py`, cited in `candidateAccess.ts`), and an `Interviewer`
   * reads this card without a single write control on it.
   */
  readonly mayAct: boolean;
  /** What this status offers, in render order — `STATUS_AFFORDANCE`, untouched by this module. */
  readonly actions: readonly StatusAction[];
  readonly act: UseMutationResult<ConsiderationAfterAction, ApiError, StatusAction>;
  /**
   * The one refusal these routes have, and it is **not** a transition rule: this person already has
   * *another* **active** run at this vacancy, so making this one active again would break
   * `uniq_active_consideration_per_person_role`. Told apart by the **field** and never by the status
   * code (`ApiError.bodyWith`, TASK-064's mechanism — there is no second error path here).
   */
  readonly existingRun: ReturnType<typeof readExistingRun>;
  /** The failure sentence, or `null` — mutually exclusive with `existingRun`. */
  readonly failure: string | null;
}

/**
 * The three status actions of one consideration as **state**, so that the control and the answer can
 * be rendered in two different places on the card and still be one mutation (§6.5, *The block's
 * composition*).
 *
 * ⚠️ **Why this is a hook and not a component.** The control lives in the block's upper strip and the
 * server's answer lives beneath that strip, above the interviews — §6.5 puts them there because the
 * control is now a **menu**, which closes itself on select: a refusal rendered inside the thing that
 * caused it would vanish with it. Two components cannot share one `useMutation`, and copying the
 * answer into both places would put two `role="alert"` nodes in the document for one failure.
 *
 * ⚠️ **Status moves through actions, never as a field.** There is no `Select` of five, no radio group
 * and no `status` key in anything sent from this card: the three calls take **no request body at all**
 * (`considerations.ts`), which is what makes a status-by-value unrepresentable here rather than merely
 * refused. That is unchanged by the move into a menu, and §6.5's composition section says at length
 * why a menu of **acts** is not the dropdown ADR 0010 refuses.
 *
 * ⚠️ **Both cache roots are invalidated, and neither is optional.** The card carries this status and
 * §6.3's table carries it too, in its Status column and its Updated column — invalidating one would
 * leave the other showing the previous status for as long as the tab lives, which is two different
 * truths about one candidate in one session. The answer body is deliberately **not** written into
 * either cache: it is `ConsiderationRowOut`, which carries none of the derived values either screen
 * renders (`ConsiderationAfterAction`), so a refetch is what keeps them honest — and `tsc` refuses
 * the write, which is what the two schemas are for.
 */
export function useStatusAction(
  consideration: ConsiderationOnCard,
  groups: AccessGroup[],
): StatusActionState {
  const { t } = useTranslation(['candidates', 'components']);
  const queryClient = useQueryClient();

  const act = useMutation<ConsiderationAfterAction, ApiError, StatusAction>({
    mutationFn: (action) => STATUS_ACTIONS[action].call(consideration.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.person });
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.considerations });
    },
  });

  const existingRun = readExistingRun(act.error);

  const failure = (() => {
    if (!act.error || existingRun) return null;
    // ⚠️ The **write** sentence, not §8's read banner (BUG-017): nothing queues a status action, and a
    // move a recruiter believes was queued is one they will not make again.
    if (act.error.isOffline) return t('components:state.offlineWrite');
    if (act.error.isThrottled) return t('components:state.throttled');
    return t('candidates:card.actions.failed');
  })();

  return {
    mayAct: mayWriteAboutPerson(groups),
    actions: actionsFor(consideration.status),
    act,
    existingRun,
    failure,
  };
}
